import React, { useState } from "react";
import { Input, Modal } from "antd";
import { PatchRequest } from "../../../../api/Request";
import { BuyApprovalStatusEnum } from "../../../../enum";
import "../styles/buyApproval.scss";

interface RejectTarget {
  _id: string;
  name: string;
  isLive: boolean;
}

export const useBuyApproval = (onSaved: () => void) => {
  const [rejectTarget, setRejectTarget] = useState<RejectTarget | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [isReviewSaving, setIsReviewSaving] = useState(false);

  const saveBuyApproval = async (
    storeId: string,
    status: BuyApprovalStatusEnum,
    reason?: string,
  ) => {
    setIsReviewSaving(true);
    const isSaved = await PatchRequest(
      `admin/v1/store/${storeId}/buy-approval`,
      { status, ...(reason ? { rejectionReason: reason } : {}) },
    )
      .then(() => true)
      .catch(() => false);
    setIsReviewSaving(false);
    if (!isSaved) return;
    setRejectTarget(null);
    setRejectionReason("");
    onSaved();
  };

  const confirmApprove = (storeId: string, storeName: string) => {
    Modal.confirm({
      title: `Approve ${storeName} for BUY?`,
      content: "The store will go live on the BUY website right away.",
      okText: "Approve",
      okButtonProps: { className: "buy-approval-modal-approve" },
      onOk: () => saveBuyApproval(storeId, BuyApprovalStatusEnum.Approved),
    });
  };

  const openReject = (target: RejectTarget) => {
    setRejectionReason("");
    setRejectTarget(target);
  };

  const rejectModal = (
    <Modal
      open={rejectTarget !== null}
      title={
        rejectTarget?.isLive
          ? `Take ${rejectTarget.name} off BUY?`
          : `Reject ${rejectTarget?.name ?? ""} for BUY?`
      }
      okText={rejectTarget?.isLive ? "Take off BUY" : "Reject"}
      okButtonProps={{
        danger: true,
        disabled: !rejectionReason.trim(),
        className: "buy-approval-modal-reject",
      }}
      confirmLoading={isReviewSaving}
      onOk={() =>
        rejectTarget &&
        saveBuyApproval(
          rejectTarget._id,
          BuyApprovalStatusEnum.Rejected,
          rejectionReason.trim(),
        )
      }
      onCancel={() => setRejectTarget(null)}
    >
      <p>
        {rejectTarget?.isLive
          ? "The store will stop showing on the BUY website. "
          : ""}
        The seller will see this reason in the Sell app and can apply again.
      </p>
      <Input.TextArea
        rows={3}
        placeholder="e.g. Please upload a clear store banner"
        value={rejectionReason}
        onChange={(e) => setRejectionReason(e.target.value)}
      />
    </Modal>
  );

  return { confirmApprove, openReject, isReviewSaving, rejectModal };
};
